import { Injectable, computed, inject, signal } from '@angular/core';
import { first } from 'rxjs';

import { ChatService } from '../features/shared/services/chat.service';
import { ChatNotificationGroup, ChatNotificationItem } from '../features/shared/models/chat-notification.type';

/** Polling backoff steps in milliseconds. */
const POLLING_STEPS_MS = [5_000, 10_000, 30_000, 60_000, 300_000] as const;

@Injectable({ providedIn: 'root' })
export class ChatNotificationStore {
  private readonly chatService = inject(ChatService);

  // ── Private state ──────────────────────────────────────────────────────────
  private readonly _items = signal<ChatNotificationItem[]>([]);
  private pollingTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentStepIndex = 0;

  // ── Public selectors ───────────────────────────────────────────────────────

  /**
   * Notifications grouped by offer (for the bell dropdown).
   * Multiple bids of the same offer are merged into one group.
   */
  readonly notificationGroups = computed<ChatNotificationGroup[]>(() => {
    const groups = new Map<string, ChatNotificationGroup>();

    for (const item of this._items()) {
      const key = item.offerId || item.bidId;
      if (!key) continue;

      const existing = groups.get(key);
      if (existing) {
        existing.totalUnread += item.unreadCount;
        if (item.bidId && !existing.bidIds.includes(item.bidId)) {
          existing.bidIds.push(item.bidId);
        }
      } else {
        groups.set(key, {
          offerId: item.offerId,
          offerNumber: item.offerNumber,
          counterpartName: item.counterpartName,
          totalUnread: item.unreadCount,
          bidIds: item.bidId ? [item.bidId] : [],
        });
      }
    }

    return Array.from(groups.values()).filter((g) => g.totalUnread > 0);
  });

  /** Returns true when there is at least one unread notification. */
  readonly hasUnread = computed(() => this.notificationGroups().length > 0);

  /** Total number of unread messages across all offers. */
  readonly totalUnread = computed(() =>
    this.notificationGroups().reduce((sum, g) => sum + g.totalUnread, 0),
  );

  /**
   * Set of bid IDs that have unread notifications.
   * Used by the bids table to highlight the message icon.
   */
  readonly unreadBidIds = computed<Set<string>>(() => {
    const ids = new Set<string>();
    for (const item of this._items()) {
      if (item.bidId && item.unreadCount > 0) ids.add(item.bidId);
    }
    return ids;
  });

  // ── Polling control ────────────────────────────────────────────────────────

  /**
   * Starts notification polling with incremental backoff.
   * Sequence: 5 s → 10 s → 30 s → 1 min → 5 min (stays at 5 min thereafter).
   */
  startPolling(): void {
    this.stopPolling();
    this.currentStepIndex = 0;
    this.fetchAndSchedule();
  }

  /** Stops polling and clears pending timeout. */
  stopPolling(): void {
    if (this.pollingTimeout !== null) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
  }

  /**
   * Returns the unread message count for a specific bid ID.
   * Returns 0 when the bid has no pending notifications.
   */
  getBidUnreadCount(bidId: string): number {
    return this._items().find((i) => i.bidId === bidId)?.unreadCount ?? 0;
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  private fetchAndSchedule(): void {
    this.fetch();

    const stepIndex = Math.min(this.currentStepIndex, POLLING_STEPS_MS.length - 1);
    const delay = POLLING_STEPS_MS[stepIndex];
    this.currentStepIndex = Math.min(this.currentStepIndex + 1, POLLING_STEPS_MS.length - 1);

    this.pollingTimeout = setTimeout(() => this.fetchAndSchedule(), delay);
  }

  private fetch(): void {
    this.chatService
      .getNotifications()
      .pipe(first())
      .subscribe({
        next: (data) => {
          this._items.set(this.mapRawNotifications(data));
        },
        error: () => {
          // Silently ignore polling errors to avoid flooding the console.
        },
      });
  }

  private mapRawNotifications(data: Record<string, unknown>[]): ChatNotificationItem[] {
    if (!Array.isArray(data)) return [];

    return data
      .map((item) => ({
        bidId: this.asString(
          item['idLance'] ?? item['lanceId'] ?? item['lance'] ?? item['bidId'] ?? item['id'] ?? '',
        ),
        offerId: this.asString(
          item['idOferta'] ?? item['ofertaId'] ?? item['oferta'] ?? item['offerId'] ?? '',
        ),
        offerNumber: this.asString(
          item['ofertaNumero'] ??
            item['numeroOferta'] ??
            item['numero'] ??
            item['offerNumber'] ??
            '',
        ),
        counterpartName: this.asString(
          item['nomeEmpresa'] ??
            item['nomeTransportadora'] ??
            item['nomeEmbarcador'] ??
            item['empresa'] ??
            item['counterpartName'] ??
            '',
        ),
        unreadCount: Number(
          item['quantidade'] ??
            item['naoLidas'] ??
            item['quantidadeNaoLidas'] ??
            item['unreadCount'] ??
            1,
        ),
      }))
      .filter((i) => i.bidId || i.offerId);
  }

  private asString(value: unknown): string {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return '';
  }
}
